import { N8nModule } from '../../types';

export const module2: N8nModule = {
  id: 'make-advanced',
  title: 'Advanced Make Features',
  description: 'Master routers, iterators, aggregators, and complex data transformations.',
  content: `# Advanced Make Features

## Routers and Conditional Logic

Routers allow your scenario to take multiple paths based on conditions.

### Setting Up Routers

1. **Add a Router Module**
   - Creates multiple branches from one point
   - Each route can have its own filter
   - Routes process independently

2. **Configure Route Filters**
   - Set conditions for each path
   - Use AND/OR logic
   - Access all previous module data

### Router Example

\`\`\`
Trigger: New Order
  Router:
    Route 1 (High Value): Amount > $1000
      → Notify VP of Sales
      → Create priority task
    
    Route 2 (Standard): Amount <= $1000 AND > $100
      → Send standard confirmation
      → Add to daily report
    
    Route 3 (Small): Amount <= $100
      → Automated processing
      → No notification
\`\`\`

## Iterators and Array Processing

Iterators process arrays of data, turning multiple items into individual operations.

### When to Use Iterators

- Processing multiple items from an API
- Handling CSV/Excel data rows
- Working with list responses
- Batch operations on collections

### Iterator Configuration

1. **Connect to Array Source**
   - Select the array field
   - Make processes each item separately

2. **Process Each Item**
   - Add modules after iterator
   - Each runs for every array item
   - Use item data in subsequent modules

### Example: Processing Email Attachments

\`\`\`
Gmail Module: Get attachments (returns array)
  ↓
Iterator: Process each attachment
  ↓
  → Check file type (Filter)
  → Upload to Google Drive
  → Send notification
\`\`\`

## Aggregators

Aggregators combine multiple operations back into a single bundle.

### Aggregator Types

1. **Array Aggregator**
   - Combines items into an array
   - Useful for creating lists
   - Batch processing results

2. **Text Aggregator**
   - Combines text strings
   - Customizable separators
   - Create formatted output

3. **Numeric Aggregator**
   - Sum, average, min, max
   - Statistical operations
   - Calculate totals

4. **Table Aggregator**
   - Create structured tables
   - Export to CSV/Excel
   - Format for reports

### Aggregator Example

\`\`\`
Search Module: Find all orders today (returns multiple)
  ↓
Iterator: Process each order
  ↓
  → Calculate commission
  → Format currency
  ↓
Numeric Aggregator: Sum all commissions
  ↓
Send Email: Daily commission report with total
\`\`\`

## Data Stores

Built-in database for storing and retrieving data.

### Data Store Features

- **Add Records**: Store data for later use
- **Search Records**: Query stored data
- **Update Records**: Modify existing data
- **Delete Records**: Remove data
- **Count Records**: Get totals

### Use Cases

1. **State Management**
   - Track processed items
   - Prevent duplicate processing
   - Store session data

2. **Lookup Tables**
   - Product information
   - Pricing data
   - Configuration values

3. **Temporary Storage**
   - Cache API responses
  - Store intermediate results
   - Queue processing tasks

## Functions and Formulas

Make includes powerful built-in functions.

### Text Functions

\`\`\`
{{upper(1.text)}}              // UPPERCASE
{{lower(1.text)}}              // lowercase
{{capitalize(1.text)}}          // Capitalize First Letter
{{substring(1.text; 0; 10)}}   // Get first 10 characters
{{replace(1.text; "old"; "new")}} // Find and replace
{{trim(1.text)}}               // Remove whitespace
\`\`\`

### Date Functions

\`\`\`
{{now}}                        // Current timestamp
{{addDays(now; 7)}}           // Add 7 days
{{formatDate(1.date; "YYYY-MM-DD")}} // Format date
{{parseDate("2024-01-01"; "YYYY-MM-DD")}} // Parse date
\`\`\`

### Mathematical Functions

\`\`\`
{{sum(1.quantity; 2.quantity)}}    // Addition
{{round(1.amount; 2)}}             // Round to 2 decimals
{{ceil(1.value)}}                  // Round up
{{floor(1.value)}}                 // Round down
\`\`\`

### Array Functions

\`\`\`
{{length(1.array)}}           // Get array size
{{first(1.array)}}            // Get first item
{{last(1.array)}}             // Get last item
{{join(1.array; ", ")}}       // Convert to string
\`\`\`

## Error Handling

Implement robust error handling in your scenarios.

### Error Handler Routes

1. **Add Error Handler**
   - Right-click module
   - Select "Add error handler"
   - Connects on errors only

2. **Error Handling Actions**
   - Send error notifications
   - Log to data store
   - Retry operations
   - Rollback changes
   - Ignore and continue

### Error Handler Example

\`\`\`
API Call Module
  ↓ (Success path)
  Process Data
  
  ↓ (Error path - Error Handler)
  Log Error to Data Store
  ↓
  Send Slack Notification
  ↓
  Commit/Rollback Module
\`\`\`

## Webhooks and Custom Integrations

### Webhooks

1. **Instant Triggers**
   - Receive real-time data
   - No polling needed
   - Immediate processing

2. **Custom Webhooks**
   - Create endpoint URL
   - Configure in external apps
   - Receive POST/GET data

### HTTP Modules

Make API requests to any service:

- **GET**: Fetch data
- **POST**: Send data
- **PUT**: Update records
- **DELETE**: Remove records
- **Custom**: Any HTTP method

## Scenario Organization

### Best Practices

1. **Use Folders**: Group related scenarios
2. **Naming Conventions**: Clear, descriptive names
3. **Color Coding**: Mark modules for clarity
4. **Add Notes**: Document complex logic
5. **Templates**: Save reusable patterns

### Performance Optimization

1. **Minimize Operations**: Combine actions when possible
2. **Use Filters Early**: Stop unnecessary processing
3. **Limit Iterations**: Set maximum items
4. **Cache Data**: Use data stores for repeated lookups
5. **Schedule Appropriately**: Run when needed`,
  duration: '150 minutes',
  order: 2,
  objectives: [
    'Implement routers and conditional paths',
    'Process arrays with iterators',
    'Aggregate data effectively',
    'Use built-in functions and formulas',
    'Handle errors gracefully'
  ],
  keyConcepts: [
    'Routers and routing',
    'Iterators and aggregators',
    'Data stores',
    'Functions and formulas',
    'Error handling'
  ]
};
